import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Store, X } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import {
  getAdminStockistSoa,
  listAdminStockistHoldings,
  listAdminStockistInvoices,
  listAdminStockistSummary,
  type StockistSummaryRow,
} from '../../lib/api';

function peso(v: string | number | null | undefined): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `₱${n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function shortDate(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
}

/** Days past the billing date, in whole days. Aging is measured from the
 *  billing itself: a stockist billing has no separate credit term. */
function agingDays(invoiceDate: string): number {
  return Math.floor((Date.now() - new Date(invoiceDate).getTime()) / 86_400_000);
}

const BUCKETS = [
  { label: 'Current', test: (d: number) => d <= 0 },
  { label: '1–30', test: (d: number) => d >= 1 && d <= 30 },
  { label: '31–60', test: (d: number) => d >= 31 && d <= 60 },
  { label: '61–90', test: (d: number) => d >= 61 && d <= 90 },
  { label: '90+', test: (d: number) => d > 90 },
] as const;

/**
 * Stockist consignment, admin side: who holds what, who owes what, and one
 * stockist's statement of account.
 *
 * Read-only by design — every document in this module is raised at the till,
 * and the receivable itself is raised by the stockist's OWN sale on our POS.
 * Do not confuse this with Stockist Receivables (`stockist-receivables.tsx`),
 * which is the older Order-by-Area feature running in the OPPOSITE direction:
 * there the house owes the stockist.
 */
export function StockistConsignmentAdminPage() {
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<StockistSummaryRow | null>(null);

  const listQ = useQuery({
    queryKey: ['admin-stockist-summary', q],
    queryFn: () => listAdminStockistSummary(q || undefined),
  });
  const soaQ = useQuery({
    queryKey: ['admin-stockist-soa', selected?.stockist_branch_id],
    queryFn: () => getAdminStockistSoa(selected!.stockist_branch_id),
    enabled: !!selected,
  });
  const holdingsQ = useQuery({
    queryKey: ['admin-stockist-holdings', selected?.stockist_branch_id],
    queryFn: () => listAdminStockistHoldings(selected!.stockist_branch_id),
    enabled: !!selected,
  });
  const invoicesQ = useQuery({
    queryKey: ['admin-stockist-invoices', selected?.stockist_branch_id],
    queryFn: () => listAdminStockistInvoices(selected!.stockist_branch_id),
    enabled: !!selected,
  });

  const rows = listQ.data ?? [];
  const openInvoices = (invoicesQ.data ?? []).filter(
    (i) => i.status === 'open' || i.status === 'partial',
  );
  const aging = BUCKETS.map((b) => ({
    label: b.label,
    total: openInvoices
      .filter((i) => b.test(agingDays(i.invoiceDate)))
      .reduce((s, i) => s + (Number(i.total) - Number(i.amountPaid)), 0),
  }));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Stockist Consignment</h1>
        <p className="text-sm text-muted-foreground">
          Company stock in stockists&apos; hands, and what they owe once it sells. Documents are
          raised at the till; billings are raised automatically by the stockist&apos;s own sale.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">Stockists</CardTitle>
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                window.setTimeout(() => setQ(e.target.value), 300);
              }}
              placeholder="Search name or code…"
              className="w-64"
            />
          </div>
        </CardHeader>
        <CardContent>
          {listQ.isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center">
              <Store className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm font-medium">No stockists holding consigned stock</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                Consign stock out from the cashier app to get started.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-3">Stockist</th>
                    <th className="py-2 pr-3">Code</th>
                    <th className="py-2 pr-3 text-right">Units out</th>
                    <th className="py-2 pr-3 text-right">Open billings</th>
                    <th className="py-2 pr-3 text-right">Receivable</th>
                    <th className="py-2">Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.stockist_branch_id}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/50"
                      onClick={() => setSelected(row)}
                    >
                      <td className="py-2.5 pr-3 font-medium">{row.name}</td>
                      <td className="py-2.5 pr-3 text-muted-foreground">{row.code}</td>
                      <td className="py-2.5 pr-3 text-right">{row.units}</td>
                      <td className="py-2.5 pr-3 text-right">{row.open_billings}</td>
                      <td className="py-2.5 pr-3 text-right font-medium">{peso(row.receivable)}</td>
                      <td className="py-2.5 text-muted-foreground">
                        {shortDate(row.last_activity)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {selected && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">
                {selected.name} · {selected.code}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
                <X className="mr-1 h-4 w-4" /> Close
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Aging of open billings
              </p>
              <div className="flex flex-wrap gap-4">
                {aging.map((b) => (
                  <div key={b.label} className="min-w-[7rem]">
                    <p className="text-xs text-muted-foreground">{b.label}</p>
                    <p className="font-medium">{peso(b.total)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Outstanding holdings
              </p>
              {(holdingsQ.data ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing consigned right now.</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {(holdingsQ.data ?? []).map((h) => (
                      <tr
                        key={`${h.productId}:${h.variantId ?? ''}`}
                        className="border-b last:border-0"
                      >
                        <td className="py-2 pr-3">
                          {h.productName}
                          {h.variantName ? (
                            <span className="text-muted-foreground"> · {h.variantName}</span>
                          ) : null}
                        </td>
                        <td className="py-2 text-right">{h.qty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Statement of account
              </p>
              {soaQ.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : soaQ.data ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="py-2 pr-3">Date</th>
                          <th className="py-2 pr-3">Document</th>
                          <th className="py-2 pr-3">Ref</th>
                          <th className="py-2 pr-3 text-right">Charges</th>
                          <th className="py-2 pr-3 text-right">Payments</th>
                          <th className="py-2 text-right">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b text-muted-foreground">
                          <td className="py-2 pr-3" colSpan={5}>
                            Opening balance
                          </td>
                          <td className="py-2 text-right">{peso(soaQ.data.openingBalance)}</td>
                        </tr>
                        {soaQ.data.lines.map((l) => (
                          <tr key={`${l.docNo}-${l.date}`} className="border-b last:border-0">
                            <td className="py-2 pr-3">{shortDate(l.date)}</td>
                            <td className="py-2 pr-3">
                              <span className="font-mono text-xs">{l.docNo}</span>{' '}
                              <span className="text-muted-foreground">{l.docType}</span>
                            </td>
                            <td className="py-2 pr-3 text-muted-foreground">{l.ref ?? '—'}</td>
                            <td className="py-2 pr-3 text-right">
                              {Number(l.charges) ? peso(l.charges) : '—'}
                            </td>
                            <td className="py-2 pr-3 text-right">
                              {Number(l.payments) ? peso(l.payments) : '—'}
                            </td>
                            <td className="py-2 text-right font-medium">{peso(l.balance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-3 text-sm">
                    Charges {peso(soaQ.data.totalCharges)} · Payments{' '}
                    {peso(soaQ.data.totalPayments)} ·{' '}
                    <span className="font-medium">Closing {peso(soaQ.data.closingBalance)}</span>
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No statement.</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
